import { c as _c } from "react/compiler-runtime";
import { styled } from '@precis-digital/kurama/utils';
import { TableRow } from '@precis-digital/kurama/Table';
import type { ColumnFiltersState, ColumnPinningState, ColumnSizingInfoState, HeaderGroup, RowSelectionState, SortingState } from '@tanstack/react-table';
import type { Dispatch, ReactElement } from 'react';
import type { ChartCardStateAction, ElementsDescriptionsMap } from '../../../internalTypes';
import type { ChartCardTFunction, ChartDataRow, ChartCardDefaults } from '../../../types';
import { useHovered } from '../hooks';
import { useChartCardDefaults } from '../../../hooks/useChartCardDefaults';
import type { ColorScaleColumnMap, ColumnSetDisplayColorScaleFn, ColumnSetSortFn } from '../types';
import TableHeadCell from './TableHeadCell';
import TableHeadCellDnD from './TableHeadCellDnD';
interface TableHeadRowProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  headerGroup: HeaderGroup<DataRow>;
  rowSelection: RowSelectionState;
  sorting: SortingState;
  setSort: ColumnSetSortFn;
  columnPinning: ColumnPinningState;
  columnFilters: ColumnFiltersState;
  isResizingColumn: ColumnSizingInfoState['isResizingColumn'];
  resizerCursorUrl: string | null;
  colorScaleColumnMap: ColorScaleColumnMap;
  setDisplayColorScale: ColumnSetDisplayColorScaleFn;
  useColumnsDecorations: boolean;
  allowColumnFiltering: boolean;
  allowDateFiltering: boolean;
  onOpenColumnFilter?: (columnId: string) => void;
  allowColumnResizing: boolean;
  allowColumnAutoResizing: boolean;
  allowColumnPinning: boolean;
  allowColumnReordering: boolean;
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
}
const TableHeadRow = t0 => {
  const $ = _c(54);
  const {
    elementsDescriptionsMap,
    headerGroup: t1,
    rowSelection,
    sorting,
    setSort,
    columnPinning,
    columnFilters,
    isResizingColumn,
    resizerCursorUrl,
    colorScaleColumnMap,
    setDisplayColorScale,
    useColumnsDecorations,
    allowColumnFiltering,
    allowDateFiltering,
    onOpenColumnFilter,
    allowColumnResizing,
    allowColumnAutoResizing,
    allowColumnPinning,
    allowColumnReordering,
    dispatch,
    t
  } = t0;
  const {
    id: groupId,
    headers
  } = t1;
  const defaults = useChartCardDefaults();
  const {
    hovered,
    onMouseEnter,
    onMouseLeave
  } = useHovered();
  const CellElement = allowColumnReordering ? TableHeadCellDnD : TableHeadCell;
  let t2;
  if ($[0] !== CellElement || $[1] !== allowColumnAutoResizing || $[2] !== allowColumnFiltering || $[3] !== allowColumnPinning || $[4] !== allowColumnReordering || $[5] !== allowColumnResizing || $[6] !== allowDateFiltering || $[7] !== colorScaleColumnMap || $[8] !== columnFilters || $[9] !== columnPinning || $[10] !== dispatch || $[11] !== elementsDescriptionsMap || $[12] !== headers || $[13] !== hovered || $[14] !== isResizingColumn || $[15] !== onOpenColumnFilter || $[16] !== resizerCursorUrl || $[17] !== rowSelection || $[18] !== setDisplayColorScale || $[19] !== setSort || $[20] !== sorting || $[21] !== t || $[22] !== useColumnsDecorations) {
    let t3;
    if ($[24] !== CellElement || $[25] !== allowColumnAutoResizing || $[26] !== allowColumnFiltering || $[27] !== allowColumnPinning || $[28] !== allowColumnReordering || $[29] !== allowColumnResizing || $[30] !== allowDateFiltering || $[31] !== colorScaleColumnMap || $[32] !== columnFilters || $[33] !== columnPinning || $[34] !== dispatch || $[35] !== elementsDescriptionsMap || $[36] !== hovered || $[37] !== isResizingColumn || $[38] !== onOpenColumnFilter || $[39] !== resizerCursorUrl || $[40] !== rowSelection || $[41] !== setDisplayColorScale || $[42] !== setSort || $[43] !== sorting || $[44] !== t || $[45] !== useColumnsDecorations) {
      t3 = header => <CellElement key={header.id} header={header} elementsDescriptionsMap={elementsDescriptionsMap} rowSelection={rowSelection} sorting={sorting} setSort={setSort} columnPinning={columnPinning} columnFilters={columnFilters} isResizingColumn={isResizingColumn} resizerCursorUrl={resizerCursorUrl} colorScaleColumnMap={colorScaleColumnMap} setDisplayColorScale={setDisplayColorScale} rowHovered={hovered} useColumnsDecorations={useColumnsDecorations} allowColumnFiltering={allowColumnFiltering} allowDateFiltering={allowDateFiltering} onOpenColumnFilter={onOpenColumnFilter} allowColumnResizing={allowColumnResizing} allowColumnAutoResizing={allowColumnAutoResizing} allowColumnPinning={allowColumnPinning} allowColumnReordering={allowColumnReordering} dispatch={dispatch} t={t} />;
      $[24] = CellElement;
      $[25] = allowColumnAutoResizing;
      $[26] = allowColumnFiltering;
      $[27] = allowColumnPinning;
      $[28] = allowColumnReordering;
      $[29] = allowColumnResizing;
      $[30] = allowDateFiltering;
      $[31] = colorScaleColumnMap;
      $[32] = columnFilters;
      $[33] = columnPinning;
      $[34] = dispatch;
      $[35] = elementsDescriptionsMap;
      $[36] = hovered;
      $[37] = isResizingColumn;
      $[38] = onOpenColumnFilter;
      $[39] = resizerCursorUrl;
      $[40] = rowSelection;
      $[41] = setDisplayColorScale;
      $[42] = setSort;
      $[43] = sorting;
      $[44] = t;
      $[45] = useColumnsDecorations;
      $[46] = t3;
    } else {
      t3 = $[46];
    }
    t2 = headers.map(t3);
    $[0] = CellElement;
    $[1] = allowColumnAutoResizing;
    $[2] = allowColumnFiltering;
    $[3] = allowColumnPinning;
    $[4] = allowColumnReordering;
    $[5] = allowColumnResizing;
    $[6] = allowDateFiltering;
    $[7] = colorScaleColumnMap;
    $[8] = columnFilters;
    $[9] = columnPinning;
    $[10] = dispatch;
    $[11] = elementsDescriptionsMap;
    $[12] = headers;
    $[13] = hovered;
    $[14] = isResizingColumn;
    $[15] = onOpenColumnFilter;
    $[16] = resizerCursorUrl;
    $[17] = rowSelection;
    $[18] = setDisplayColorScale;
    $[19] = setSort;
    $[20] = sorting;
    $[21] = t;
    $[22] = useColumnsDecorations;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[47] !== defaults || $[48] !== groupId || $[49] !== hovered || $[50] !== onMouseEnter || $[51] !== onMouseLeave || $[52] !== t2) {
    t3 = <StyledTableHeadRow key={groupId} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} hovered={hovered} defaults={defaults}>{t2}</StyledTableHeadRow>;
    $[47] = defaults;
    $[48] = groupId;
    $[49] = hovered;
    $[50] = onMouseEnter;
    $[51] = onMouseLeave;
    $[52] = t2;
    $[53] = t3;
  } else {
    t3 = $[53];
  }
  return t3;
};
const StyledTableHeadRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'hovered' && prop !== 'defaults'
})<{
  hovered: boolean;
  defaults: ChartCardDefaults;
}>(({
  hovered,
  defaults
}) => ({
  display: 'flex',
  width: '100%',
  backgroundColor: hovered ? defaults.table.hoverBackgroundColor.head ?? defaults.table.backgroundColor.head : defaults.table.backgroundColor.head
}));
export default TableHeadRow;